import React, { useState } from 'react';
import { toast } from 'react-toastify';

function DownloadCSVButton({ data, headers, filename }) {
  const download = () => {
    if (!data || !data.length) return;
    const rows = [headers, ...data];
    const csvContent = rows.map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
    URL.revokeObjectURL(url);
    toast.success(`${filename} downloaded`);
  };

  return (
    <button className="btn btn-primary btn-sm" onClick={download} disabled={!data || !data.length}>
      📥 Download CSV
    </button>
  );
}

export default function ReportViewer({ reportData, activeReport, onBack, generating }) {
  if (!activeReport) return null;

  const overview = reportData?.overview || {};

  const headerStyle = { fontWeight: 600, color: '#64748b', fontSize: '13px' };

  const renderContent = () => {
    if (generating) {
      return (
        <div style={{ textAlign: 'center', padding: '60px', color: '#64748b' }}>
          <div style={{ fontSize: '14px' }}>Loading report data...</div>
        </div>
      );
    }

    if (!reportData) {
      return <p style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>No data available. Try again.</p>;
    }

    switch (activeReport) {
      case 'performance': {
        const recruiters = reportData.topRecruiters || [];
        return (
          <div>
            <div className="table-container">
              <table className="table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Recruiter Name</th>
                    <th>Referral Code</th>
                    <th>Admissions</th>
                    <th>Enrollments</th>
                    <th>Total Commission</th>
                  </tr>
                </thead>
                <tbody>
                  {recruiters.map((r, i) => (
                    <tr key={r.id}>
                      <td>{i + 1}</td>
                      <td style={{ fontWeight: 600 }}>{r.full_name}</td>
                      <td><span className="badge badge-info">{r.referral_code}</span></td>
                      <td>{r.admission_count}</td>
                      <td>{r.enrollment_count}</td>
                      <td style={{ color: '#037b90', fontWeight: 600 }}>KES {Number(r.total_commission || 0).toFixed(2)}</td>
                    </tr>
                  ))}
                  {!recruiters.length && (
                    <tr><td colSpan={6} style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>No recruiter data available</td></tr>
                  )}
                </tbody>
              </table>
            </div>
            <div style={{ padding: '16px', display: 'flex', gap: '8px' }}>
              <DownloadCSVButton
                data={recruiters.map(r => [r.full_name, r.referral_code, r.admission_count, r.enrollment_count, `KES ${Number(r.total_commission || 0).toFixed(2)}`])}
                headers={['Recruiter Name', 'Referral Code', 'Admissions', 'Enrollments', 'Total Commission']}
                filename={`recruiter_performance_${new Date().toISOString().split('T')[0]}.csv`}
              />
            </div>
          </div>
        );
      }

      case 'regional': {
        const data = reportData.regionalData || [];
        return (
          <div>
            <div className="table-container">
              <table className="table">
                <thead>
                  <tr>
                    <th>County</th>
                    <th>Recruiters</th>
                  </tr>
                </thead>
                <tbody>
                  {data.map((r, i) => (
                    <tr key={i}>
                      <td style={{ fontWeight: 600 }}>{r.region}</td>
                      <td>{r.count}</td>
                    </tr>
                  ))}
                  {!data.length && (
                    <tr><td colSpan={2} style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>No regional data available</td></tr>
                  )}
                </tbody>
              </table>
            </div>
            <div style={{ padding: '16px', display: 'flex', gap: '8px' }}>
              <DownloadCSVButton
                data={data.map(r => [r.region, r.count])}
                headers={['County', 'Recruiters']}
                filename={`regional_distribution_${new Date().toISOString().split('T')[0]}.csv`}
              />
            </div>
          </div>
        );
      }

      case 'growth': {
        const data = reportData.monthlyPerformance || reportData.monthlySummary || [];
        return (
          <div>
            <div className="table-container">
              <table className="table">
                <thead>
                  <tr>
                    <th>Month</th>
                    <th>Admissions</th>
                    <th>Enrollments</th>
                    <th>Revenue</th>
                  </tr>
                </thead>
                <tbody>
                  {data.map((r, i) => (
                    <tr key={i}>
                      <td style={{ fontWeight: 600 }}>{r.month_name || r.month}</td>
                      <td>{r.admissions || 0}</td>
                      <td>{r.enrollments || 0}</td>
                      <td style={{ color: '#037b90', fontWeight: 600 }}>KES {Number(r.revenue || r.total_commission || 0).toFixed(2)}</td>
                    </tr>
                  ))}
                  {!data.length && (
                    <tr><td colSpan={4} style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>No growth trend data available</td></tr>
                  )}
                </tbody>
              </table>
            </div>
            <div style={{ padding: '16px', display: 'flex', gap: '8px' }}>
              <DownloadCSVButton
                data={data.map(r => [r.month_name || r.month, r.admissions || 0, r.enrollments || 0, `KES ${Number(r.revenue || r.total_commission || 0).toFixed(2)}`])}
                headers={['Month', 'Admissions', 'Enrollments', 'Revenue']}
                filename={`growth_trends_${new Date().toISOString().split('T')[0]}.csv`}
              />
            </div>
          </div>
        );
      }

      case 'export': {
        const summaryRows = [
          ['Active Recruiters', overview.activeRecruiters || 0],
          ['Total Admissions', overview.totalAdmissions || 0],
          ['Total Enrollments', overview.totalEnrollments || 0],
          ['Total Commissions', `KES ${Number(overview.totalCommissions || 0).toFixed(2)}`],
          ['Pending Payments', overview.pendingPayments || 0],
          ['Pending Amount', `KES ${Number(overview.pendingAmount || 0).toFixed(2)}`]
        ];
        return (
          <div>
            <div className="table-container">
              <table className="table">
                <thead>
                  <tr>
                    <th>Metric</th>
                    <th>Value</th>
                  </tr>
                </thead>
                <tbody>
                  {summaryRows.map((r, i) => (
                    <tr key={i}>
                      <td style={{ fontWeight: 600 }}>{r[0]}</td>
                      <td>{r[1]}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div style={{ padding: '16px', display: 'flex', gap: '8px' }}>
              <DownloadCSVButton
                data={summaryRows}
                headers={['Metric', 'Value']}
                filename={`full_data_export_${new Date().toISOString().split('T')[0]}.csv`}
              />
            </div>
          </div>
        );
      }

      default:
        return null;
    }
  };

  return (
    <div className="main-content">
      <div className="page-header">
        <div>
          <h1 className="page-title">
            {activeReport === 'performance' ? 'Recruiter Performance Report' :
             activeReport === 'regional' ? 'Regional Distribution Report' :
             activeReport === 'growth' ? 'Growth Trends Report' :
             'Full Data Export'}
          </h1>
          <p className="page-subtitle">Data sourced from live dashboard</p>
        </div>
        <button className="btn btn-secondary" onClick={onBack}>
          ← Back to Reports
        </button>
      </div>

      <div className="card">
        <div className="card-body" style={{ padding: '24px' }}>
          {renderContent()}
        </div>
      </div>
    </div>
  );
}

export function ReportCards({ onGenerate }) {
  const cards = [
    { id: 'performance', icon: '📊', title: 'Recruiter Performance Report', desc: 'Top recruiters, admission stats, and commission data' },
    { id: 'regional', icon: '🗺️', title: 'Regional Distribution Report', desc: 'Recruiter and student distribution by county' },
    { id: 'growth', icon: '📈', title: 'Growth Trends Report', desc: 'Monthly admission and enrollment trends' },
    { id: 'export', icon: '📁', title: 'Export All Data', desc: 'Download comprehensive data export for external use' }
  ];

  return (
    <div className="dashboard-grid">
      {cards.map(card => (
        <div key={card.id} className="card" style={{ cursor: 'pointer' }} onClick={() => onGenerate(card.id)}>
          <div className="card-body" style={{ textAlign: 'center', padding: '40px' }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>{card.icon}</div>
            <h3>{card.title}</h3>
            <p style={{ color: '#64748b', fontSize: '14px' }}>{card.desc}</p>
          </div>
        </div>
      ))}
    </div>
  );
}
